import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AddCircleOutline as AddIcon,
  Cancel as CancelIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmationDialog from "../../../shared/ui/ConfirmationDialog";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

function computePreview(basePrice, ruleType, markupPercent, flatAmount) {
  const pct = parseFloat(markupPercent) || 0;
  const flat = parseFloat(flatAmount) || 0;
  if (!basePrice) return { finalPrice: 0, diff: 0, marginPct: 0 };

  let finalPrice;
  if (ruleType === "Markup") finalPrice = basePrice * (1 + pct / 100) + flat;
  else finalPrice = basePrice * (1 - pct / 100) - flat;

  const diff = finalPrice - basePrice;
  const marginPct = basePrice > 0 ? (diff / basePrice) * 100 : 0;
  return { finalPrice: Math.max(0, finalPrice), diff, marginPct };
}

const EMPTY_FORM = {
  packageId: "",
  ruleName: "",
  ruleType: "Markup",
  markupPercent: "",
  flatAmount: "",
  notes: "",
  isActive: true,
};

export default function DistributorPricingForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [tours, setTours] = useState([]);
  const [rules, setRules] = useState([]);
  const [loadingTours, setLoadingTours] = useState(true);
  const [loadingRules, setLoadingRules] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  const selectedTour = tours.find((t) => t.id === form.packageId) || null;
  const { finalPrice, diff, marginPct } = computePreview(
    selectedTour?.basePrice || 0,
    form.ruleType,
    form.markupPercent,
    form.flatAmount
  );

  const fetchRules = useCallback(async () => {
    setLoadingRules(true);
    try {
      const res = await fetch(`${API_BASE}/api/pricing-rules`, { headers: authHeaders() });
      if (!res.ok) throw new Error("Failed to load rules");
      setRules(await res.json());
    } catch {
      toast.error("Could not load pricing rules.");
    } finally {
      setLoadingRules(false);
    }
  }, []);

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/packages?limit=200`);
        const data = await res.json();
        setTours(
          (data.data || data).map((t) => ({
            id: t._id,
            name: t.basicInfo.tour_name,
            basePrice: t.pricing.pricePerPerson || 0,
            currency: t.pricing.currency || "NGN",
          }))
        );
      } catch {
        toast.error("Could not load tour packages.");
      } finally {
        setLoadingTours(false);
      }
    };
    fetchTours();
    fetchRules();
  }, [fetchRules]);

  const validate = () => {
    const e = {};
    if (!form.packageId) e.packageId = "Select a tour package.";
    if (!form.ruleName.trim()) e.ruleName = "Rule name is required.";
    if (!form.ruleType) e.ruleType = "Select a rule type.";
    if (form.markupPercent !== "" && (isNaN(form.markupPercent) || Number(form.markupPercent) < 0)) {
      e.markupPercent = "Enter a valid percentage (≥ 0).";
    }
    if (form.flatAmount !== "" && (isNaN(form.flatAmount) || Number(form.flatAmount) < 0)) {
      e.flatAmount = "Enter a valid amount (≥ 0).";
    }
    if (finalPrice <= 0 && form.packageId) {
      e.flatAmount = "This configuration results in a zero or negative selling price.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);

    const payload = {
      packageId: form.packageId,
      ruleName: form.ruleName.trim(),
      ruleType: form.ruleType,
      markupPercent: parseFloat(form.markupPercent) || 0,
      flatAmount: parseFloat(form.flatAmount) || 0,
      isActive: form.isActive,
      notes: form.notes.trim(),
    };

    try {
      const url = editingId
        ? `${API_BASE}/api/pricing-rules/${editingId}`
        : `${API_BASE}/api/pricing-rules`;
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed.");

      toast.success(editingId ? "Rule updated successfully." : "Pricing rule created.");
      resetForm();
      fetchRules();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (rule) => {
    const pkgId = typeof rule.package === "object" ? rule.package?._id : rule.package;
    setForm({
      packageId: pkgId || "",
      ruleName: rule.ruleName,
      ruleType: rule.ruleType,
      markupPercent: rule.markupPercent ?? "",
      flatAmount: rule.flatAmount ?? "",
      notes: rule.notes || "",
      isActive: rule.isActive,
    });
    setEditingId(rule._id);
    setErrors({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleToggle = async (rule) => {
    try {
      const res = await fetch(`${API_BASE}/api/pricing-rules/${rule._id}/toggle`, {
        method: "PATCH",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Toggle failed.");
      toast.success(`Rule ${rule.isActive ? "deactivated" : "activated"}.`);
      fetchRules();
    } catch {
      toast.error("Could not update rule status.");
    }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/pricing-rules/${deleteTarget}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Delete failed.");
      toast.success("Rule deleted.");
      fetchRules();
    } catch {
      toast.error("Could not delete rule.");
    } finally {
      setDeleteTarget(null);
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setErrors({});
  };

  const currency = selectedTour?.currency || "NGN";
  const basePrice = selectedTour?.basePrice || 0;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: "auto" }}>
      <Box mb={4}>
        <Typography variant="h5" fontWeight={700}>
          Markup Manager
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Set your selling prices for tour packages. Customers see only your final price.
        </Typography>
      </Box>

      <Grid container spacing={3} alignItems="flex-start">
        {/* ── Form ── */}
        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
            <CardHeader
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AddIcon color="primary" />
                  <Typography fontWeight={700}>
                    {editingId ? "Edit Pricing Rule" : "New Pricing Rule"}
                  </Typography>
                </Stack>
              }
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Stack spacing={2.5}>
                <TextField
                  select
                  label="Tour Package *"
                  name="packageId"
                  value={form.packageId}
                  onChange={handleChange}
                  disabled={!!editingId}
                  error={!!errors.packageId}
                  helperText={errors.packageId || (editingId ? "Package cannot be changed when editing." : "")}
                  fullWidth
                >
                  {loadingTours ? (
                    <MenuItem disabled>Loading packages…</MenuItem>
                  ) : (
                    tours.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        <Stack>
                          <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Net rate: {t.currency} {Number(t.basePrice).toLocaleString()}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))
                  )}
                </TextField>

                <TextField
                  label="Rule Name *"
                  name="ruleName"
                  value={form.ruleName}
                  onChange={handleChange}
                  error={!!errors.ruleName}
                  helperText={errors.ruleName || "e.g. Standard Markup, Peak Season Rate"}
                  fullWidth
                />

                <TextField
                  select
                  label="Rule Type *"
                  name="ruleType"
                  value={form.ruleType}
                  onChange={handleChange}
                  error={!!errors.ruleType}
                  helperText={errors.ruleType}
                  fullWidth
                >
                  <MenuItem value="Markup">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TrendingUpIcon fontSize="small" color="success" />
                      <span>Markup — sell above net rate</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="Markdown">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TrendingDownIcon fontSize="small" color="warning" />
                      <span>Markdown — sell below net rate</span>
                    </Stack>
                  </MenuItem>
                </TextField>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Percentage"
                      name="markupPercent"
                      type="number"
                      value={form.markupPercent}
                      onChange={handleChange}
                      error={!!errors.markupPercent}
                      helperText={errors.markupPercent || "Applied to net rate"}
                      fullWidth
                      inputProps={{ min: 0, step: 0.1 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Flat Amount"
                      name="flatAmount"
                      type="number"
                      value={form.flatAmount}
                      onChange={handleChange}
                      error={!!errors.flatAmount}
                      helperText={errors.flatAmount || "Fixed fee added after %"}
                      fullWidth
                      inputProps={{ min: 0 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">{currency}</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <TextField
                  label="Notes"
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  placeholder="Internal notes about this rule (optional)"
                  fullWidth
                />

                <FormControlLabel
                  control={
                    <Switch
                      name="isActive"
                      checked={form.isActive}
                      onChange={handleChange}
                      color="success"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {form.isActive ? "Active — visible to customers" : "Inactive — hidden from customers"}
                    </Typography>
                  }
                />

                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                  {editingId && (
                    <Button variant="outlined" color="inherit" onClick={resetForm} startIcon={<CancelIcon />}>
                      Cancel
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    sx={{ minWidth: 160 }}
                  >
                    {submitting ? (
                      <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : editingId ? (
                      "Update Rule"
                    ) : (
                      "Save Pricing Rule"
                    )}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* ── Live Preview ── */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: form.packageId ? "primary.main" : "divider",
              position: "sticky",
              top: 80,
            }}
          >
            <CardHeader
              title="Price Preview"
              titleTypographyProps={{ fontWeight: 700, variant: "subtitle1" }}
              subheader={selectedTour ? selectedTour.name : "Select a package to see preview"}
              sx={{ pb: 0 }}
            />
            <CardContent>
              {!selectedTour ? (
                <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: 2 }}>
                  Choose a tour package above to see a live breakdown of your selling price.
                </Alert>
              ) : (
                <Stack spacing={0}>
                  {/* Net Rate Row */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
                    <Typography variant="body2" color="text.secondary">Net rate (base)</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {currency} {Number(basePrice).toLocaleString()}
                    </Typography>
                  </Box>
                  <Divider />

                  {/* Percentage Row */}
                  {(parseFloat(form.markupPercent) || 0) !== 0 && (
                    <>
                      <Box sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {form.ruleType === "Markup" ? "+" : "−"} {form.markupPercent}% percentage
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={form.ruleType === "Markup" ? "success.main" : "warning.main"}
                        >
                          {form.ruleType === "Markup" ? "+" : "−"}{currency}{" "}
                          {Math.abs((basePrice * (parseFloat(form.markupPercent) || 0)) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                      <Divider />
                    </>
                  )}

                  {/* Flat Amount Row */}
                  {(parseFloat(form.flatAmount) || 0) !== 0 && (
                    <>
                      <Box sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {form.ruleType === "Markup" ? "+" : "−"} flat amount
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={form.ruleType === "Markup" ? "success.main" : "warning.main"}
                        >
                          {form.ruleType === "Markup" ? "+" : "−"}{currency}{" "}
                          {Number(parseFloat(form.flatAmount) || 0).toLocaleString()}
                        </Typography>
                      </Box>
                      <Divider />
                    </>
                  )}

                  {/* Final Price */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 1.5,
                      mt: 0.5,
                      bgcolor: finalPrice > 0 ? "primary.50" : "error.50",
                      px: 1.5,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body1" fontWeight={700}>
                      Your selling price
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      color={finalPrice > 0 ? "primary.main" : "error.main"}
                    >
                      {currency} {Number(finalPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>

                  {/* Margin summary */}
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      bgcolor: diff >= 0 ? "success.50" : "warning.50",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {diff >= 0 ? (
                      <TrendingUpIcon fontSize="small" color="success" />
                    ) : (
                      <TrendingDownIcon fontSize="small" color="warning" />
                    )}
                    <Typography variant="caption" fontWeight={600}>
                      {diff >= 0 ? "Margin" : "Discount"}: {currency}{" "}
                      {Math.abs(diff).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                      per person ({Math.abs(marginPct).toFixed(1)}%{" "}
                      {diff >= 0 ? "above" : "below"} net rate)
                    </Typography>
                  </Box>

                  {finalPrice <= 0 && (
                    <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, py: 0.5 }}>
                      Selling price cannot be zero or negative.
                    </Alert>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Rules Table ── */}
      <Card sx={{ mt: 5, borderRadius: 3 }}>
        <CardHeader
          title="Your Pricing Rules"
          titleTypographyProps={{ fontWeight: 700 }}
          subheader={
            !loadingRules
              ? `${rules.length} rule${rules.length !== 1 ? "s" : ""} · ${rules.filter((r) => r.isActive).length} active`
              : ""
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {loadingRules ? (
            <Stack spacing={1}>
              {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={52} />)}
            </Stack>
          ) : rules.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No pricing rules yet. Create your first rule above to start selling tours.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Rule</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tour Package</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Net Rate</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Markup</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Selling Price</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Margin</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((rule) => {
                    const pkg = rule.package;
                    const tourName = pkg?.basicInfo?.tour_name || "Deleted package";
                    const base = rule.basePrice || 0;
                    const cur = rule.currency || "NGN";
                    const margin = rule.finalPrice - base;
                    const marginPctRow = base > 0 ? (margin / base) * 100 : 0;
                    const sign = rule.ruleType === "Markup" ? "+" : "−";
                    const markupLabel = [
                      rule.markupPercent ? `${rule.markupPercent}%` : null,
                      rule.flatAmount ? `${sign}${cur} ${Number(rule.flatAmount).toLocaleString()}` : null,
                    ]
                      .filter(Boolean)
                      .join(" + ") || "—";

                    return (
                      <TableRow
                        key={rule._id}
                        sx={{
                          opacity: rule.isActive ? 1 : 0.55,
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{rule.ruleName}</Typography>
                          {rule.notes && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {rule.notes}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{tourName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={rule.ruleType}
                            size="small"
                            color={rule.ruleType === "Markup" ? "success" : "warning"}
                            variant="outlined"
                            icon={
                              rule.ruleType === "Markup" ? (
                                <TrendingUpIcon sx={{ fontSize: "14px !important" }} />
                              ) : (
                                <TrendingDownIcon sx={{ fontSize: "14px !important" }} />
                              )
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {cur} {Number(base).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{markupLabel}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700} color="primary.main">
                            {cur} {Number(rule.finalPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            color={margin >= 0 ? "success.main" : "warning.main"}
                          >
                            {margin >= 0 ? "+" : ""}{cur} {Math.abs(margin).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            <br />({marginPctRow >= 0 ? "+" : ""}{marginPctRow.toFixed(1)}%)
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={rule.isActive ? "Active" : "Inactive"}
                            size="small"
                            color={rule.isActive ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0} justifyContent="center">
                            <Tooltip title="Edit rule">
                              <IconButton size="small" onClick={() => handleEdit(rule)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={rule.isActive ? "Deactivate" : "Activate"}>
                              <IconButton
                                size="small"
                                color={rule.isActive ? "success" : "default"}
                                onClick={() => handleToggle(rule)}
                              >
                                {rule.isActive ? (
                                  <ToggleOnIcon fontSize="small" />
                                ) : (
                                  <ToggleOffIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete rule">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteTarget(rule._id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Pricing Rule"
        description="This will permanently remove the rule. Customers will no longer see this tour in your listings until a new rule is created."
        confirmText="Delete"
        confirmColor="error"
      />

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
    </Box>
  );
}
